import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Ask Your Church to Support You as a Missionary (Step-by-Step)';
const DESCRIPTION =
    "A practical, step-by-step guide to approaching your pastor and missions committee about becoming a sent and supported missionary — what to bring, what they'll ask, and how to follow up.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover19.jpg',
            datePublished: '2026-07-24',
            dateModified: '2026-07-24',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-your-church-to-support-you-as-a-missionary',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Who should I talk to first about becoming a supported missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Start with your pastor or, if your church has one, the missions or outreach pastor — not the missions committee directly. A one-on-one conversation lets you test the idea, learn how your church's process actually works, and get a sponsor who can advocate for you before it goes in front of a group.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What does a missions committee actually want to see?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Clarity, not polish: a specific call and location, a realistic monthly budget, evidence you've thought through logistics (visa, housing, team), and a plan for how you'll communicate with supporters once you're on the field. Vague passion without a concrete plan is the most common reason a committee stalls a request.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What if my church can't fully fund me?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Full funding from one church is the exception, not the norm, for most missionaries. Ask your home church to be your sending church — praying for you, endorsing you publicly, and giving what it can — while you raise the remainder from individual supporters and other churches. A strong sending-church endorsement is often what makes other churches and donors willing to give.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How long does the process usually take?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "From first conversation with a pastor to a committee decision, plan for one to three months at most churches — longer if your church requires a formal missions policy vote or board approval. Building in that runway, rather than assuming a quick yes, avoids unnecessary pressure on both sides.",
                    },
                },
            ],
        },
    ],
};

export default function HowToAskYourChurchToSupportYouAsAMissionary() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-your-church-to-support-you-as-a-missionary' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-ask-church-support-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover19.jpg" alt="A pastor and a missionary candidate talking after a church service" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries don't struggle with the calling — they struggle with the conversation. Walking up to your pastor and saying "I think God wants me on the field, and I need your church behind me" is intimidating, and most guides skip straight past it to letter-writing and budgets. Here's how that conversation actually goes, step by step.</p>

                    <h2>Start with your pastor, not the committee</h2>

                    <p>Don't take your first ask straight to a missions committee or a Sunday announcement. Start with a private conversation with your pastor, or your church's missions/outreach pastor if it has one. This does three things: it lets you test and sharpen the idea with someone who knows the church's actual process, it gives you a chance to be corrected or redirected early and privately, and — if it goes well — it gives you a sponsor inside the room when your request does go to a committee or elder board.</p>

                    <p>Come to that first meeting with a rough one-page summary, not a full proposal: where you feel called, why, what you'd be doing, and roughly when. You're not asking for a decision yet. You're asking, "Does this make sense, and what's our church's process for something like this?"</p>

                    <h2>Learn the process before you present</h2>

                    <p>Every church handles missionary support differently. Some have a standing missions committee that reviews and votes on requests quarterly. Some route everything through the elder board. Some have no formal process at all, and the pastor's endorsement is effectively the whole decision. Ask directly: "What's the process here, and who actually makes this call?" Skipping this step is the single most common reason candidates feel blindsided later — they prepared the pitch they imagined, not the one their church actually required.</p>

                    <h2>What a missions committee is actually evaluating</h2>

                    <p>Committees aren't grading your spiritual maturity in the room — they're checking whether a request is specific and thought-through enough to say yes to. In practice that means:</p>

                    <p><strong>A specific call and location.</strong> "Missions" isn't specific enough. Where, doing what, with whom (an agency, a partner church, independently), and why there.</p>

                    <p><strong>A realistic monthly budget.</strong> Living costs, insurance, travel, ministry expenses, and a platform or admin fee if you're using one. Committees trust round, padded numbers less than a number you can walk them through line by line.</p>

                    <p><strong>A logistics plan.</strong> Visa status, housing, language prep, and — if you're going with family — schooling and healthcare. You don't need every answer solved, but you need to show you've asked the questions.</p>

                    <p><strong>A communication plan.</strong> How you'll keep supporters updated once you're on the field. This is where a simple, low-maintenance system matters more than people expect — a committee that isn't confident supporters will hear from you regularly is a committee that hesitates to endorse you publicly.</p>

                    <h2>Ask for the right thing: sending, not just funding</h2>

                    <p>Full financial support from one home church is rare — most missionaries raise support across dozens of individual donors and several churches, with one "sending church" at the center. So the specific ask matters. Instead of "will you fully fund me," ask your home church to become your <strong>sending church</strong>: to pray for you by name, commission you publicly, give what it realistically can toward your budget, and let you present to the congregation for individual support. That combination — a public endorsement plus partial funding — is usually worth more than the dollar amount alone, because other churches and individual donors take their cues from whether your own church is behind you.</p>

                    <p>If you're building the letter or presentation that goes along with this ask, our guide to <a href="/resources/how-to-write-a-missionary-support-letter">writing a missionary support letter that gets you funded</a> covers the individual-donor side of the same conversation.</p>

                    <h2>Follow up like it's a real decision, because it is</h2>

                    <p>After the first conversation, send a short written summary — one page, the same content you talked through, so your pastor has something concrete to forward to a committee or board. Then ask directly when you should expect to hear back, and put that date on your own calendar to follow up. Committees are slow by nature, not because they're avoiding you; a polite, specific check-in ("just following up ahead of your meeting on the 12th") moves things along far more than waiting silently.</p>

                    <p>Plan for the whole process — first conversation to committee decision — to take one to three months at most churches, longer if a formal vote or budget cycle is involved. Building that runway into your own timeline keeps the wait from feeling like a rejection when it's really just process.</p>

                    <h2>If the answer is partial, or not yet</h2>

                    <p>A "not right now" or a smaller commitment than you hoped for isn't the end of the conversation. Ask what would need to be true for a larger commitment later — a track record on the field, a growing individual-donor base, a specific project. Churches often move from "not yet" to "yes" once they see a missionary actually executing: showing up on the field, communicating consistently, and building support from other sources. Treat the first ask as the opening of an ongoing relationship, not a one-time pitch.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Who should I talk to first about becoming a supported missionary?</strong><br />
                    Your pastor or missions/outreach pastor, one-on-one, before it goes to a committee or the congregation.</p>

                    <p><strong>What does a missions committee actually want to see?</strong><br />
                    A specific call and location, a realistic budget, a logistics plan, and a communication plan — clarity, not polish.</p>

                    <p><strong>What if my church can't fully fund me?</strong><br />
                    Ask them to be your sending church: prayer, public endorsement, and partial funding, while you raise the rest from individual supporters and other churches.</p>

                    <p><strong>How long does the process usually take?</strong><br />
                    Plan for one to three months from first conversation to a committee decision, longer if a formal vote is required.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We talk to missionaries at every stage of raising support, from the first conversation with a pastor to years into recurring giving — this guide is the conversation we hear about most often before anyone opens a fundraising page.</p>
                    <p><small><em>Last updated: 2026-07-24.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-your-church-to-support-you-as-a-missionary" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
